import {
  Component,
  ElementRef,
  OnInit,
  ViewChild,
  inject,
  ChangeDetectionStrategy,
} from '@angular/core'
import {
  FormBuilder,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms'
import { MatButtonModule } from '@angular/material/button'
import { MatOptionModule } from '@angular/material/core'
import { MatFormFieldModule } from '@angular/material/form-field'
import { MatIcon } from '@angular/material/icon'
import { MatInputModule } from '@angular/material/input'
import { MatSelectModule } from '@angular/material/select'
import { IngredientService } from '../../../services/ingredient.service'
import { Dialog } from '@angular/cdk/dialog'
import { DialogService } from '../../../services/dialog.service'
import { ErrorService } from '../../../services/error.service'
import { RouteService } from '../../../services/route.service'
import { SpinnerService } from '../../../services/spinner.service'
import { TranslationService } from '../../../services/translation.service'
import { IngredientCsvTemplate } from '../../../types/csvTemplates'
import { ROUTES } from '../../../constants/routes'
import { firstValueFrom } from 'rxjs/internal/firstValueFrom'
import { IngredientCsvTemplatesService } from '../../../services/ingredientCsvTemplate.service'
import { ApiError } from '../../../types/apiError'

@Component({
  selector: 'app-import-ingredients',
  imports: [
    MatFormFieldModule,
    FormsModule,
    ReactiveFormsModule,
    MatInputModule,
    MatOptionModule,
    MatSelectModule,
    MatButtonModule,
    MatIcon,
  ],
  templateUrl: './import-ingredients.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './import-ingredients.component.scss',
})
export class ImportIngredientsComponent implements OnInit {
  private fb = inject(FormBuilder)
  private dialog = inject(Dialog)
  private dialogService = inject(DialogService)
  private routeService = inject(RouteService)
  private ingredientService = inject(IngredientService)
  translationService = inject(TranslationService)
  private templateService = inject(IngredientCsvTemplatesService)
  private spinnerService = inject(SpinnerService)
  private errorService = inject(ErrorService)

  @ViewChild('ingredientImportFile') ingredientImportFile?: ElementRef

  fileData: Record<string, File | File[]> = {}

  templates: Array<IngredientCsvTemplate> = []

  importForm = this.fb.group({
    template: ['', Validators.required],
  })

  async ngOnInit() {
    const currentRoute = this.routeService.getUserRoutes(
      ROUTES.INGREDIENTS.title,
      ROUTES.INGREDIENTS.path,
    )
    this.routeService.setRoute(currentRoute)

    await this.loadTemplates()
  }

  /**
   * Fetches csv templates for selection
   */
  async loadTemplates() {
    try {
      this.spinnerService.startSpinner()
      this.templates = await firstValueFrom(this.templateService.getAll())
      this.spinnerService.stopSpinner()
    } catch (err) {
      this.spinnerService.stopSpinner()
      const apiErr = err as ApiError
      this.errorService.handleErr(this.dialogService, apiErr, this.dialog)
    }
  }

  /**
   * Resets form and selected files
   */
  reset(): void {
    this.importForm.reset()

    if (this.ingredientImportFile) {
      this.ingredientImportFile.nativeElement.value = ''
    }
  }

  /**
   * Handles file change
   * @param {Event} event File change event
   * @param {string} fieldName Name of the file(s)
   */
  onFileChange(event: Event, fieldName: string): void {
    const input = event.target as HTMLInputElement

    if (input.files) {
      if (input.multiple) {
        this.fileData[fieldName] = Array.from(input.files)
      } else {
        this.fileData[fieldName] = input.files[0]
      }
    }
  }

  /**
   * Validates form, creates formData and submits request to import files and get products for review
   * @returns {Promise<void>}
   */
  async onSubmit(): Promise<void> {
    if (this.importForm.invalid) {
      console.error('Form is invalid')
      return
    }

    try {
      this.spinnerService.startSpinner()
      const formData = new FormData()

      formData.append('templateId', this.importForm.value.template!)

      if (this.ingredientImportFile?.nativeElement.files?.[0]) {
        const file = this.ingredientImportFile.nativeElement.files[0] as File
        formData.append('ingredientImportFile', file)
      }

      await firstValueFrom(this.ingredientService.importIngredients(formData))

      this.dialogService.showDialog(
        this.dialog,
        this.translationService.getImportIngredientsText(),
        this.translationService.getImportIngredientsSuccessText(),
      )

      this.reset()

      this.spinnerService.stopSpinner()
    } catch (err) {
      this.spinnerService.stopSpinner()
      const apiErr = err as ApiError
      this.errorService.handleErr(this.dialogService, apiErr, this.dialog)
    }
  }
}
